import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowRight, Hexagon } from "lucide-react";
import FeaturedGallery from "@/components/FeaturedGallery";
import FeaturedThisWeek from "@/components/FeaturedThisWeek";
import ScrollSpyDots from "@/components/ScrollSpyDots";
import SocialProofRotator from "@/components/SocialProofRotator";
import SupportDialog from "@/components/SupportDialog";
import { openConsentBanner } from "@/lib/cookieConsent";
import Hero from "@/components/landing/Hero";
import HowItWorks from "@/components/landing/HowItWorks";
import FeatureShowcase from "@/components/landing/FeatureShowcase";
import UseCases from "@/components/landing/UseCases";
import Testimonials from "@/components/landing/Testimonials";
import FoundersStory from "@/components/landing/FoundersStory";
import LandingPricing from "@/components/landing/LandingPricing";

const SPY_SECTIONS = [
  { id: "spy-hero",          label: "Hero" },
  { id: "spy-how",           label: "How" },
  { id: "spy-features",      label: "Features" },
  { id: "spy-use-cases",     label: "For you" },
  { id: "spy-testimonials",  label: "Loved" },
  { id: "spy-founders",      label: "Story" },
  { id: "spy-pricing",       label: "Pricing" },
  { id: "spy-cta",           label: "Join" },
];

const DEFAULTS = {
  hero_eyebrow: "For Beekeepers",
  hero_title: "A quiet home for your bees.",
  hero_subtitle: "Track every hive, log inspections, set reminders, and diagnose issues from a single photo — built for hobbyists and pro apiarists alike.",
  cta_primary_label: "Start your journal",
  cta_primary_link: "/register",
  cta_secondary_label: "I already have an account",
  cta_secondary_link: "/login",
  features: [
    { icon: "notebook", label: "Inspection logs" },
    { icon: "bell", label: "Smart reminders" },
    { icon: "camera", label: "AI diagnosis" },
  ],
  inspection_card: { title: "Last inspection", subtitle: "Hive Linden · Strong", body: "Brood pattern solid. Queen seen." },
  upcoming_card: { title: "Upcoming", subtitle: "Varroa check", body: "Sun, 10:00 AM" },
  footer_text: "Built for bee lovers. Mind the pollinators.",
  hero_image_url: "https://images.unsplash.com/photo-1770211828861-32f49b848f45?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

const pub = axios.create({ baseURL: API });

export default function Landing() {
  const [c, setC] = useState(DEFAULTS);

  useEffect(() => {
    pub.get("/public/site/landing")
      .then((r) => setC({ ...DEFAULTS, ...r.data, features: (r.data.features?.length ? r.data.features : DEFAULTS.features) }))
      .catch(() => setC(DEFAULTS));
  }, []);

  return (
    <div className="min-h-screen bg-stone-50" data-testid="landing-page">
      <header className="max-w-7xl mx-auto flex items-center justify-between px-6 py-5">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-yellow-400 grid place-items-center">
            <Hexagon className="w-5 h-5 text-amber-900" strokeWidth={2.5} />
          </div>
          <span className="font-display text-xl font-semibold tracking-tight">Hive <span className="text-yellow-500">@</span> Buzzzillion</span>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/login"><Button variant="ghost" className="rounded-full" data-testid="nav-login">Sign in</Button></Link>        </div>
      </header>

      <Hero c={c} />

      <HowItWorks />

      <FeatureShowcase />

      <UseCases />

      <Testimonials />

      <FoundersStory />

      <LandingPricing />

      {/* "Featured this week" hidden until enough accounts exist — re-enable by uncommenting */}
      {/* <div id="spy-featured"><FeaturedThisWeek /></div> */}

      {/* Hive of the Week gallery hidden until enough accounts exist — re-enable by uncommenting */}
      {/* <div id="spy-gallery"><FeaturedGallery /></div> */}

      <section id="spy-cta" className="max-w-5xl mx-auto px-6 py-20 text-center" data-testid="landing-cta-strip">
        <div className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-700 mb-3">Ready when you are</div>
        <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight">Managing your hive just got easier.</h2>
        <p className="text-stone-600 mt-3 max-w-xl mx-auto">Free to start. Photos, journal, reminders, and an AI second opinion — for hobbyists and pros alike.</p>
        <div className="flex flex-wrap gap-3 justify-center mt-6">
          <Link to={c.cta_primary_link || "/register"}>
            <Button size="lg" className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300 px-6" data-testid="cta-strip-primary">
              {c.cta_primary_label} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link to={c.cta_secondary_link || "/login"}>
            <Button size="lg" variant="outline" className="rounded-full px-6 border-stone-300" data-testid="cta-strip-secondary">
              {c.cta_secondary_label}
            </Button>
          </Link>
        </div>
        <SocialProofRotator />
      </section>

      <ScrollSpyDots sections={SPY_SECTIONS} />

      <footer className="footer-dark mt-12" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-6 py-12 grid sm:grid-cols-3 gap-8 items-start">
          <div className="sm:col-span-1">
            <div className="flex items-center gap-2 mb-2">
              <Hexagon className="w-4 h-4 text-yellow-300 fill-yellow-300/40" />
              <span className="font-display text-base text-amber-50">Hive @ Buzzzillion</span>
            </div>
            <p className="text-xs text-stone-300 leading-relaxed max-w-xs">{c.footer_text}</p>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-amber-200/80 mb-3">Explore</div>
            <ul className="space-y-1.5 text-sm text-stone-300">
              <li><Link to="/forum" className="hover:text-amber-300 transition-colors">Community forum</Link></li>
              <li><Link to="/app/guide" className="hover:text-amber-300 transition-colors">Beekeeping guide</Link></li>
              <li><a href="#spy-pricing" className="hover:text-amber-300 transition-colors" data-testid="footer-pricing-link">Plans & pricing</a></li>
              <li><Link to="/faq" className="hover:text-amber-300 transition-colors" data-testid="footer-faq-link">FAQ</Link></li>
              <li><Link to="/register" className="hover:text-amber-300 transition-colors">Create an account</Link></li>
              <li><Link to="/login" className="hover:text-amber-300 transition-colors">Sign in</Link></li>
            </ul>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-amber-200/80 mb-3">Help</div>
            <ul className="space-y-1.5 text-sm text-stone-300">
              <li><SupportDialog>Support</SupportDialog></li>
              <li><Link to="/policy" className="hover:text-amber-300 transition-colors" data-testid="footer-policy-link">Terms & Content Policy</Link></li>
              <li><Link to="/cookie-policy" className="hover:text-amber-300 transition-colors" data-testid="footer-cookie-policy-link">Cookie policy</Link></li>
              <li>
                <button
                  type="button"
                  onClick={openConsentBanner}
                  className="hover:text-amber-300 transition-colors text-left"
                  data-testid="footer-manage-cookies"
                >
                  Manage cookie preferences
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-amber-950/50 max-w-7xl mx-auto px-6 py-4 text-[11px] text-stone-400 flex items-center justify-between flex-wrap gap-2">
          <span>© {new Date().getFullYear()} Hive @ Buzzzillion · Built for bee lovers.</span>
          <span className="text-stone-500">Tend your hives. Track every drop.</span>
        </div>
      </footer>
    </div>
  );
}
